/**
 * PreviewVerticalCard Component
 *
 * Shows preview of vertical card with full overlay content.
 * Recreates the card overlay (title, metadata, buttons) from submission data.
 * Uses SubmissionImage for proper R2 presigned URL handling.
 *
 * Features:
 * - Uses verticalTileUrl if available, falls back to posterUrl
 * - Shows CardOverlay with title, year, runtime, genres, director
 * - Hover effects and gradient overlays enabled
 * - Navigation disabled (preview only)
 */

import type { ReactElement } from "react";
import { Button } from "@lumi/ui-headless";
import { Info, Play } from "lucide-react";
import { useCascadeFadeIn } from "../../../../shared/hooks";
import { SubmissionImage } from "../../components/SubmissionImage";
import type { TitlePreviewData } from "./TitlePreview";

interface PreviewVerticalCardProps {
  readonly data: TitlePreviewData;
  readonly submissionId?: string | null;
  readonly titleId?: string | null;
  readonly index?: number;
}

export function PreviewVerticalCard({
  data,
  submissionId,
  titleId,
  index = 0,
}: PreviewVerticalCardProps): ReactElement {
  const { ref, isVisible } = useCascadeFadeIn({ index });

  // Determine which image to use - prefer vertical tile, fallback to poster
  const imageUrl = data.verticalTileUrl ?? data.posterUrl;

  return (
    <div
      ref={ref}
      className={`
                group flex-shrink-0 self-start text-left w-[calc(100vw-3rem)] sm:w-[400px] snap-center
                ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}
            `}
      style={{
        transition: "opacity 500ms ease-out, transform 500ms ease-out",
      }}
    >
      {/* Outer wrapper with shadow - enhanced on hover */}
      <div className="card-base card-hover-vertical">
        <div className="relative aspect-[2/3] overflow-hidden rounded-lg bg-neutral-900 ring-1 ring-neutral-800/50">
          {/* Poster image via SubmissionImage for R2 support */}
          {imageUrl && (
            <SubmissionImage
              src={imageUrl}
              alt={data.title}
              className="h-full w-full object-cover"
              submissionId={submissionId}
              titleId={titleId}
            />
          )}

          {/* Diagonal gradient overlays on hover - dark from bottom-left, light from top-right */}
          {/* No rotation/scaling on touch devices to prevent overflow */}
          <div className="card-gradient-light" />
          <div className="card-gradient-dark" />

          {/* Card overlay content - shown on hover */}
          <div className="absolute inset-0 flex flex-col justify-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-out z-10">
            <div className="space-y-2">
              <h2 className="font-semibold text-white text-2xl leading-tight line-clamp-2">{data.title}</h2>

              <div className="flex items-center gap-1.5 text-xs">
                {data.year && <span className="text-neutral-300 font-medium">{data.year}</span>}
                {data.year && data.runtime && <span className="text-neutral-500">•</span>}
                {data.runtime && <span className="text-neutral-300 font-medium">{data.runtime}m</span>}
                {(data.year || data.runtime) && data.genres && data.genres.length > 0 && (
                  <span className="text-neutral-500">•</span>
                )}
                {data.genres && data.genres.length > 0 && (
                  <span className="text-neutral-400 truncate">{data.genres.slice(0, 2).join(", ")}</span>
                )}
              </div>

              {data.director && (
                <p className="text-xs text-neutral-400 truncate">
                  Directed by <span className="text-neutral-300 font-medium truncate">{data.director}</span>
                </p>
              )}

              <div className="flex gap-0 justify-start pt-1 pointer-events-none">
                <Button variant="ghost-sm" className="pl-0 pointer-events-auto" onClick={() => {}}>
                  <Play size={16} />
                  <span className="ml-1.5 text-sm font-semibold">Play</span>
                </Button>
                <Button variant="ghost-sm" className="pl-0 pointer-events-auto" onClick={() => {}}>
                  <Info size={16} />
                  <span className="ml-1.5 text-sm font-semibold">More Info</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
